// UnitModal.jsx
import { useState, useMemo } from 'react';
import {
  Alert,
  View,
  Flex,
  TextField,
  TextAreaField,
  Button,
} from '@aws-amplify/ui-react';
import SimpleDialog from '../common/SimpleDialog';
import { normalizeCode } from '../../utils/codeUtils';
import { normalizeGraphQLError } from '../../utils/graphqlErrors';
import { getFieldError } from '../../utils/formErrors';

export default function UnitModal({
  open,
  onClose,
  onCreated,
  createUnitFn,        // caller passes the actual GraphQL call
  unitProperty,        // selected property object
  unitBuilding,        // selected building object
}) {
  const maxNameLen = 80, maxNotesLen = 500;

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [touched, setTouched] = useState({
    code: false,
    name: false,
    notes: false,
  });


  const validation = useMemo(() => {
    const v = {};

    const cleanCode = normalizeCode(code);
    const cleanName = name.trim();
    const cleanNotes = notes.trim();

    if (!cleanCode) {
      v.code = 'Code is required.';
    } else if (cleanCode.length < 1 || cleanCode.length > 32) {
      v.code = 'Code must be 1–32 chars.';
    }

    if (cleanName && cleanName.length > maxNameLen) {
      v.name = `Keep name under ${maxNameLen} characters.`;
    }

    if ((cleanNotes || '').length > maxNotesLen) {
      v.notes = `Notes must be ≤ ${maxNotesLen} chars.`;
    }

    return v;
  }, [code, name, notes]);

  const hasErrors = Object.values(validation).some(Boolean);

  async function handleSubmit(e) {
    e.preventDefault();
    
    setTouched({ code: true, name: true, notes: true });

    if (hasErrors) return;

    setSubmitting(true);

    const payload = {
      code: normalizeCode(code),
      name: name.trim() || null,
      notes: notes.trim() || null
    };

    try {
      const input = {
        propertyId: unitProperty?.id,
        buildingId: unitBuilding?.id,
        ...payload,
      };
      const u = await createUnitFn(input);

      // Reset local state
      setCode('');
      setName('');
      setNotes('');
      setErrors({});
      setTouched({ name: false, code: false, notes: false });
      onCreated?.(u);
      onClose?.();
    } catch (err) {
      console.error('Failed to create unit:', err);

      const mapped = normalizeGraphQLError(err);

      setErrors(mapped);
    } finally {
      setSubmitting(false);
    }
  }

  const title = `New Unit for ${unitProperty?.name ?? 'selected property'} › ${unitBuilding?.code ?? unitBuilding?.name ?? 'selected building'}`;

  const codeError = getFieldError({
    field: "code",
    touched,
    validation,
    errors
  });

  const nameError = getFieldError({
    field: "name",
    touched,
    validation,
    errors
  });

  const notesError = getFieldError({
    field: "notes",
    touched,
    validation,
    errors
  });

  return (
    <SimpleDialog open={open} onClose={onClose} title={title}>
      <View as="form" onSubmit={handleSubmit} noValidate>
        <Flex direction="column" gap="0.75rem">
          {errors.global && (
            <Alert
              variation="error"
              isDismissible
              onDismiss={() => setErrors(e => ({ ...e, global: undefined }))}
              heading="Couldn't create unit"
            >
              {errors.global}
            </Alert>
          )}

          <TextField
            label="Code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (errors.code) setErrors(prev => ({ ...prev, code: undefined }));
            }}
            onBlur={() => {
              setTouched((t) => ({ ...t, code: true }));
              setCode((s) => normalizeCode(s));
            }}
            placeholder="e.g., 4B or A-12"
            descriptiveText="Short and unique within the selected building."
            hasError={!!codeError}
            errorMessage={codeError || undefined}
            isRequired
            isDisabled={submitting}
            autoFocus
            autoComplete="off"
          />

          <TextField
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() =>
              setTouched((t) => ({ ...t, name: true }))
            }
            placeholder="e.g., Penthouse"
            hasError={!!nameError}
            errorMessage={nameError || undefined}
            isDisabled={submitting}
            autoComplete="off"
          />

          <TextAreaField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() =>
              setTouched((t) => ({ ...t, notes: true }))
            }
            hasError={!!notesError}
            errorMessage={notesError || undefined}
            rows={3}
            isDisabled={submitting}
            autoComplete="off"
          />

          <Flex justifyContent="flex-end" gap="0.5rem" marginTop="0.5rem">
            <Button type="button" onClick={onClose} variation="link" isDisabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variation="primary" isLoading={submitting}>
              Create
            </Button>
          </Flex>
        </Flex>
      </View>
    </SimpleDialog>
  );
}
